import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
const shippingFeeByZone = {
  local: 80,
  region: 100,
  national: 125,
};

const initialForm = {
  cost: 200,
  packaging: 10,
  sellingPrice: 600,
  commissionPercent: 12,
  shippingZone: "national",
  shippingFee: 125,
  offerValue: 0,
  offerType: "fixed",
  gstPercentOnFees: 18,
  marketing: 20,
  returnsRatePercent: 5,
  courierReturnPercent: 5,
  defectiveRatePercent: 2,
  perDayOrder: 50,
  fixedMonthlyOtherCost: 10000,
};

const calculateUnitEconomics = (data) => {
  const {
    cost,
    packaging,
    sellingPrice,
    commissionPercent,
    shippingFee,
    gstPercentOnFees,
    marketing,
    returnsRatePercent,
    courierReturnPercent,
    defectiveRatePercent,
    perDayOrder,
    fixedMonthlyOtherCost,
    offerValue,
    offerType,
  } = data;

  const landedCost = Number(cost) + Number(packaging);

  let effectiveSellingPrice = Number(sellingPrice);
  if (offerType === "fixed") {
    effectiveSellingPrice = Math.max(0, sellingPrice - offerValue);
  } else if (offerType === "percent") {
    effectiveSellingPrice = Math.max(
      0,
      sellingPrice - (sellingPrice * offerValue) / 100
    );
  }

  const commission = (commissionPercent / 100) * effectiveSellingPrice;
  const gstOnFees = (commission * gstPercentOnFees) / 100;

  const totalFees = commission + shippingFee + gstOnFees;

  const grossProfit =
    effectiveSellingPrice - (landedCost + totalFees + marketing);
  const profitMargin =
    effectiveSellingPrice > 0 ? (grossProfit / effectiveSellingPrice) * 100 : 0;

  const perDayDeliveredOrders =
    perDayOrder -
    (perDayOrder * (returnsRatePercent + courierReturnPercent)) / 100;

  const customer = (perDayOrder * returnsRatePercent) / 100;
  const courier = (perDayOrder * returnsRatePercent) / 100;
  const defective = (perDayOrder * defectiveRatePercent) / 100;

  const returnsImpact =
    ((Number(packaging) + shippingFee * 2) * customer) / perDayDeliveredOrders;

  const returnsImpactCourier =
    (Number(packaging) * courier) / perDayDeliveredOrders;

  const defectiveImpact = (Number(cost) * defective) / perDayDeliveredOrders;

  const monthlyOrders = perDayOrder * 30;

  const fixedCostPerUnit =
    monthlyOrders > 0 ? fixedMonthlyOtherCost / monthlyOrders : 0;

  const netProfit =
    grossProfit -
    returnsImpact -
    returnsImpactCourier -
    defectiveImpact -
    fixedCostPerUnit;

  const netProfitMargin =
    effectiveSellingPrice > 0 ? (netProfit / effectiveSellingPrice) * 100 : 0;

  const dailyNetProfit = netProfit * perDayOrder;
  const monthlyNetProfit = dailyNetProfit * 30;

  const breakEvenSellingPrice =
    landedCost +
    totalFees +
    marketing +
    returnsImpact +
    returnsImpactCourier +
    defectiveImpact +
    fixedCostPerUnit +
    (offerType === "fixed" ? offerValue : (sellingPrice * offerValue) / 100);

  return {
    landedCost,
    effectiveSellingPrice,
    grossProfit,
    profitMargin,
    netProfit,
    dailyNetProfit,
    monthlyNetProfit,
    breakEvenSellingPrice,
    fixedCostPerUnit,
    returnsImpact,
    defectiveImpact,
    commission,
    returnsImpactCourier,
    gstOnFees,
    netProfitMargin,
    perDayDeliveredOrders,
  };
};

const FlipkartUnitEconomics = () => {
  const [form, setForm] = useState(initialForm);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};

    const numberFields = [
      "cost",
      "packaging",
      "sellingPrice",
      "commissionPercent",
      "gstPercentOnFees",
      "marketing",
      "returnsRatePercent",
      "courierReturnPercent",
      "defectiveRatePercent",
      "perDayOrder",
      "fixedMonthlyOtherCost",
      "offerValue",
    ];

    numberFields.forEach((field) => {
      if (form[field] === "" || form[field] === null || isNaN(form[field])) {
        newErrors[field] = "This field is required";
      } else if (form[field] < 0) {
        newErrors[field] = "Cannot be negative";
      }
    });

    const percentFields = [
      "commissionPercent",
      "gstPercentOnFees",
      "returnsRatePercent",
      "defectiveRatePercent",
      "courierReturnPercent",
    ];
    percentFields.forEach((field) => {
      if (form[field] < 0 || form[field] > 100) {
        newErrors[field] = "Must be between 0 and 100";
      }
    });

    if (form.offerType === "percent" && form.offerValue > 100) {
      newErrors.offerValue = "Offer percent cannot exceed 100%";
    }

    if (form.sellingPrice <= 0) {
      newErrors.sellingPrice = "Selling Price must be greater than 0";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };
  const handleChange = (e) => {
    const { name, value } = e.target;

    const numericValue = e.target.type === "number" ? Number(value) : value;

    if (name === "shippingZone") {
      setForm((prev) => ({
        ...prev,
        [name]: numericValue,
        shippingFee: shippingFeeByZone[numericValue],
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const calcResult = calculateUnitEconomics(form);
    setResult(calcResult);
  };

  return (
    <Box>
      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h4" mb={2} fontWeight="bold">
              Seller Unit Economics Calculator
            </Typography>
            <Grid container spacing={2}>
              {Object.keys(initialForm).map((key) =>
                key === "offerType" ? (
                  <Grid item xs={12} sm={6} md={3} key={key}>
                    <TextField
                      select
                      fullWidth
                      label="Offer Type"
                      name={key}
                      size="small"
                      value={form[key]}
                      onChange={handleChange}
                      error={!!errors[key]}
                      helperText={errors[key]}
                    >
                      <MenuItem value="fixed">Fixed (₹)</MenuItem>
                      <MenuItem value="percent">Percent (%)</MenuItem>
                    </TextField>
                  </Grid>
                ) : key === "shippingZone" ? (
                  <Grid item xs={12} sm={6} md={3} key={key}>
                    <TextField
                      select
                      fullWidth
                      label="Shipping Zone"
                      name={key}
                      size="small"
                      value={form[key]}
                      onChange={handleChange}
                      error={!!errors[key]}
                      helperText={errors[key]}
                    >
                      <MenuItem value="local">Local</MenuItem>
                      <MenuItem value="region">Region</MenuItem>
                      <MenuItem value="national">National</MenuItem>
                    </TextField>
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6} md={3} key={key}>
                    <TextField
                      fullWidth
                      label={key
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                      name={key}
                      size="small"
                      type={
                        typeof initialForm[key] === "number" ? "number" : "text"
                      }
                      value={form[key]}
                      onChange={handleChange}
                      error={!!errors[key]}
                      helperText={errors[key]}
                    />
                  </Grid>
                )
              )}
            </Grid>

            <Box mt={2}>
              <Button type="submit" variant="contained" color="primary">
                Calculate
              </Button>
            </Box>
          </CardContent>
        </Card>
      </form>
      {result && (
        <Card elevation={4}>
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Results Summary (Detailed Breakdown)
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Per Day Delivered Orders :{" "}
                  {result.perDayDeliveredOrders?.toFixed(2)}
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  Selling Price (₹ {result.effectiveSellingPrice?.toFixed(2)})
                </Typography>
                <Typography>
                  Base Selling Price : ₹ {form.sellingPrice}
                </Typography>
                <Typography>
                  Offer Applied ({form.offerType}{" "}
                  {form.offerType === "percent" ? "" : "₹"} {form.offerValue}
                  {form.offerType === "percent" ? "%" : ""}) →
                  <strong> ₹ {result.effectiveSellingPrice?.toFixed(2)}</strong>
                </Typography>
              </Grid>

              <Divider flexItem sx={{ my: 1 }} />

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Cost Structure (₹ {result.landedCost?.toFixed(2)})
                </Typography>
                <Typography>Product Cost : ₹ {form.cost}</Typography>
                <Typography>Packaging Cost : ₹ {form.packaging}</Typography>
                <Typography>
                  Landed Cost (Product + Packaging) →
                  <strong> ₹ {result.landedCost?.toFixed(2)}</strong>
                </Typography>
              </Grid>

              <Divider flexItem sx={{ my: 1 }} />

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Marketplace Fees (₹{" "}
                  {(
                    result.commission +
                    form.shippingFee +
                    result.gstOnFees
                  )?.toFixed(2)}
                  )
                </Typography>
                <Typography>
                  Commission ({form.commissionPercent}%) : ₹{" "}
                  {result?.commission?.toFixed(2)}
                </Typography>
                <Typography>
                  Shipping Fee ({form.shippingZone}) : ₹ {form.shippingFee}
                </Typography>
                <Typography>
                  GST on Fees ({form.gstPercentOnFees}%) : ₹{" "}
                  {result?.gstOnFees?.toFixed(2)}
                </Typography>
              </Grid>

              <Divider flexItem sx={{ my: 1 }} />

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Other Deductions (₹{" "}
                  {(
                    form.marketing +
                    result.returnsImpact +
                    result?.returnsImpactCourier +
                    result.defectiveImpact +
                    result.fixedCostPerUnit
                  )?.toFixed(2)}
                  )
                </Typography>
                <Typography>Marketing Spend : ₹ {form.marketing}</Typography>
                <Typography>
                  Customer Returns Impact {form.returnsRatePercent}% returns : ₹{" "}
                  {result?.returnsImpact?.toFixed(2)}{" "}
                  <Tooltip title="Packaging loss & reversible shipping charges">
                    <InfoIcon sx={{ fontSize: 15 }} />
                  </Tooltip>
                </Typography>
                <Typography>
                  Courier Returns Impact {form.courierReturnPercent}% returns :
                  ₹ {result?.returnsImpactCourier?.toFixed(2)}{" "}
                  <Tooltip title="Packaging loss">
                    <InfoIcon sx={{ fontSize: 15 }} />
                  </Tooltip>
                </Typography>
                <Typography>
                  Defective Impact ({form.defectiveRatePercent}% defective) : ₹{" "}
                  {result.defectiveImpact.toFixed(2)}
                </Typography>
                <Typography>
                  Fixed Monthly Costs per Unit : ₹{" "}
                  {result.fixedCostPerUnit?.toFixed(2)}
                </Typography>
              </Grid>

              <Divider flexItem sx={{ my: 1 }} />

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Profitability
                </Typography>
                <Typography>
                  Gross Profit (before returns/defective/fixed costs) →
                  <strong> ₹ {result.grossProfit?.toFixed(2)}</strong>
                </Typography>
                <Typography>
                  Net Profit (after all costs) →
                  <strong> ₹ {result.netProfit?.toFixed(2)}</strong>
                </Typography>
                <Typography>
                  Gross Profit Margin →
                  <strong> {result.profitMargin?.toFixed(2)}%</strong>
                </Typography>
                <Typography>
                  Net Profit Margin →
                  <strong> {result.netProfitMargin?.toFixed(2)}%</strong>
                </Typography>
                <Typography>
                  Break-even Selling Price →
                  <strong> ₹ {result.breakEvenSellingPrice?.toFixed(2)}</strong>
                </Typography>
              </Grid>

              <Divider flexItem sx={{ my: 1 }} />

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Order Projections
                </Typography>
                <Typography>
                  Daily Net Profit ({form.perDayOrder} orders) :
                  <strong> ₹ {result.dailyNetProfit?.toFixed(2)}</strong>
                </Typography>
                <Typography>
                  Monthly Net Profit (30 days) :
                  <strong> ₹ {result.monthlyNetProfit?.toFixed(2)}</strong>
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FlipkartUnitEconomics;
